import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Bot, Plus, Save, Trash2, ArrowLeft, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

type TradingPair = {
  symbol: string;
  base_asset: string;
  quote_asset: string;
  is_active: boolean;
  min_qty: number;
  price_precision: number;
  qty_precision: number;
};

export default function TradingPairs() {
  const [pairs, setPairs] = useState<TradingPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingPair, setEditingPair] = useState<Partial<TradingPair> | null>(null);

  useEffect(() => {
    checkAdminStatus();
    loadTradingPairs();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();
        
        setIsAdmin(profile?.is_admin || false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const loadTradingPairs = async () => {
    try {
      const { data, error } = await supabase
        .from('trading_pairs')
        .select('*')
        .order('quote_asset', { ascending: true })
        .order('base_asset', { ascending: true });

      if (error) throw error;
      setPairs(data || []);
    } catch (error: any) {
      toast.error('Failed to load trading pairs');
      console.error('Error:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPair = () => {
    setEditingPair({
      symbol: '',
      base_asset: '',
      quote_asset: 'USDT',
      is_active: true,
      min_qty: 0.001,
      price_precision: 2,
      qty_precision: 6
    });
  };

  const handleSavePair = async () => {
    if (!editingPair?.symbol || !editingPair?.base_asset || !editingPair?.quote_asset) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('trading_pairs')
        .upsert({
          ...editingPair,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast.success(editingPair.symbol ? 'Pair updated successfully' : 'Pair added successfully');
      setEditingPair(null);
      loadTradingPairs();
    } catch (error: any) {
      toast.error('Failed to save trading pair');
      console.error('Error:', error.message);
    }
  };

  const handleDeletePair = async (symbol: string) => {
    if (!confirm('Are you sure you want to delete this trading pair?')) return;

    try {
      const { error } = await supabase
        .from('trading_pairs')
        .delete()
        .eq('symbol', symbol);

      if (error) throw error;

      toast.success('Trading pair deleted successfully');
      loadTradingPairs();
    } catch (error: any) {
      toast.error('Failed to delete trading pair');
      console.error('Error:', error.message);
    }
  };

  // Group pairs by quote asset
  const groupedPairs = pairs.reduce((acc, pair) => {
    const group = pair.quote_asset === 'USDT' ? 'USDT' : 'USD.P';
    if (!acc[group]) acc[group] = [];
    acc[group].push(pair);
    return acc;
  }, {} as Record<string, TradingPair[]>);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-white">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/admin"
            className="flex items-center text-white hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Admin
          </Link>
          <h2 className="text-2xl font-bold text-white">Available Trading Pairs</h2>
        </div>
        <button
          onClick={handleAddPair}
          className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Pair</span>
        </button>
      </div>

      {/* Info Message */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex items-start space-x-3">
        <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300">
          <p className="font-medium text-blue-400 mb-1">Trading Pairs Management</p>
          <p>This section manages the master list of available trading pairs that can be assigned to bots. Each pair's settings affect:</p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Minimum order quantities</li>
            <li>Price and quantity precision</li>
            <li>System-wide pair availability</li>
          </ul>
          <p className="mt-2">Individual bots can be configured to use specific pairs from this list.</p>
        </div>
      </div>

      {editingPair && (
        <div className="bg-blue-900/40 backdrop-blur-sm rounded-lg p-6 border border-blue-500/20">
          <h3 className="text-xl font-semibold text-white mb-6">
            {editingPair.symbol ? 'Edit Trading Pair' : 'New Trading Pair'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Base Asset *
              </label>
              <input
                type="text"
                value={editingPair.base_asset}
                onChange={(e) => {
                  const base = e.target.value.toUpperCase();
                  setEditingPair({
                    ...editingPair,
                    base_asset: base,
                    symbol: `${base}${editingPair.quote_asset}`
                  });
                }}
                className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                placeholder="BTC"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Quote Asset *
              </label>
              <select
                value={editingPair.quote_asset}
                onChange={(e) => {
                  const quote = e.target.value;
                  setEditingPair({
                    ...editingPair,
                    quote_asset: quote,
                    symbol: `${editingPair.base_asset}${quote}`
                  });
                }}
                className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
              >
                <option value="USDT">USDT</option>
                <option value="USD.P">USD.P</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Minimum Quantity
              </label>
              <input
                type="number"
                value={editingPair.min_qty}
                onChange={(e) => setEditingPair({ ...editingPair, min_qty: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                step="0.000001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Price Precision
              </label>
              <input
                type="number"
                value={editingPair.price_precision}
                onChange={(e) => setEditingPair({ ...editingPair, price_precision: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                min="0"
                max="8"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Quantity Precision
              </label>
              <input
                type="number"
                value={editingPair.qty_precision}
                onChange={(e) => setEditingPair({ ...editingPair, qty_precision: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-black/20 border border-gray-600 rounded-md text-white"
                min="0"
                max="8"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={editingPair.is_active}
                onChange={(e) => setEditingPair({ ...editingPair, is_active: e.target.checked })}
                className="h-4 w-4 text-cyan-500"
              />
              <label className="text-sm text-gray-200">Active</label>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={() => setEditingPair(null)}
              className="px-4 py-2 text-white hover:text-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSavePair}
              className="flex items-center space-x-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
            >
              <Save className="h-5 w-5" />
              <span>Save Pair</span>
            </button>
          </div>
        </div>
      )}

      {/* USDT Pairs */}
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">USDT Pairs</h3>
        <div className="grid grid-cols-1 gap-4">
          {groupedPairs['USDT']?.map((pair) => (
            <div
              key={pair.symbol}
              className={`bg-blue-900/40 backdrop-blur-sm rounded-lg p-4 border ${
                pair.is_active ? 'border-blue-500/20' : 'border-gray-500/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Bot className="h-8 w-8 text-cyan-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">{pair.base_asset}/USDT</h3>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        pair.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {pair.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingPair(pair)}
                    className="text-cyan-400 hover:text-cyan-300"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeletePair(pair.symbol)}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <p className="text-sm text-gray-400">Minimum Quantity</p>
                  <p className="text-lg font-semibold text-white">{pair.min_qty}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Price Precision</p>
                  <p className="text-lg font-semibold text-white">{pair.price_precision}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Quantity Precision</p>
                  <p className="text-lg font-semibold text-white">{pair.qty_precision}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* USD.P Pairs */}
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">USD.P Pairs</h3>
        <div className="grid grid-cols-1 gap-4">
          {groupedPairs['USD.P']?.map((pair) => (
            <div
              key={pair.symbol}
              className={`bg-blue-900/40 backdrop-blur-sm rounded-lg p-4 border ${
                pair.is_active ? 'border-blue-500/20' : 'border-gray-500/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Bot className="h-8 w-8 text-cyan-400" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">{pair.base_asset}/USD.P</h3>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        pair.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {pair.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingPair(pair)}
                    className="text-cyan-400 hover:text-cyan-300"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeletePair(pair.symbol)}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <p className="text-sm text-gray-400">Minimum Quantity</p>
                  <p className="text-lg font-semibold text-white">{pair.min_qty}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Price Precision</p>
                  <p className="text-lg font-semibold text-white">{pair.price_precision}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Quantity Precision</p>
                  <p className="text-lg font-semibold text-white">{pair.qty_precision}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
